import React, { useState, useMemo, useEffect, useRef } from 'react';
import Pagination from './Pagination';
import { API } from '../api';
import './UsersSection.css';

const UsersSection = ({ 
  users = [], 
  pagination, 
  onPageChange,
  loading, 
  onAddHours, 
  onViewPayments, 
  currency, 
  onUserNameClick, 
  showPagination = true 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(pagination ? pagination.page : 1);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const itemsPerPage = pagination ? pagination.limit : 15;
  const isMounted = useRef(false);

  // Sync currentPage with pagination prop
  useEffect(() => {
    if (pagination) {
      setCurrentPage(pagination.page);
    }
  }, [pagination]);

  // Debounce search effect
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim()) {
        setIsSearching(true);
        try {
          const result = await API.users.search(searchQuery, 5);
          if (result.success) {
            // Handle both array and object wrapper with data property
            const usersData = Array.isArray(result.data) 
              ? result.data 
              : (result.data && Array.isArray(result.data.data)) 
                ? result.data.data 
                : [];
            setSearchResults(usersData);
          } else {
            setSearchResults([]);
          }
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const filteredUsers = useMemo(() => {
    if (searchQuery.trim()) {
      return searchResults;
    }
    return users;
  }, [users, searchQuery, searchResults]);

  const { paginatedUsers, totalPages } = useMemo(() => {
    if (searchQuery.trim()) {
      // Search results (usually small number, show all)
      return {
        paginatedUsers: searchResults,
        totalPages: 1
      };
    }

    if (pagination) {
      // Server-side pagination
      return {
        paginatedUsers: users,
        totalPages: Math.ceil((pagination.total || 0) / (pagination.limit || 5))
      };
    }

    // Client-side pagination fallback
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      paginatedUsers: filteredUsers.slice(start, end),
      totalPages: Math.ceil(filteredUsers.length / itemsPerPage)
    };
  }, [filteredUsers, currentPage, pagination, itemsPerPage, searchQuery, searchResults, users]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (!searchQuery.trim() && pagination && onPageChange) {
      onPageChange(page, itemsPerPage);
    }
  };

  const isLoading = loading || isSearching;

  return (
    <div className="users-section">
      <div className="section-header">
        <h2>Пользователи</h2>
        <div className="search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Поиск по имени, username или ID..."
            value={searchQuery}
            onChange={(e) => {
              const val = e.target.value;
              setSearchQuery(val);
              setCurrentPage(1);
              if (!val.trim() && pagination && onPageChange) {
                onPageChange(1, itemsPerPage);
              }
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{isSearching ? 'Поиск...' : 'Загрузка пользователей...'}</p>
        </div>
      ) : filteredUsers.length === 0 && !searchQuery ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <p>Пользователи не найдены</p>
        </div>
      ) : filteredUsers.length === 0 && searchQuery ? (
         <div className="empty-state">
           <p>Ничего не найдено</p>
         </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Username</th>
                  <th>Telegram ID</th>
                  <th>Доступные часы</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map(user => (
                  <tr key={user.id} className="user-row">
                    <td>
                      <div className="user-info-cell">
                        {user.avatarUrl && (
                          <img 
                            src={user.avatarUrl} 
                            alt={user.firstName} 
                            className="user-avatar-small"
                          />
                        )}
                        <button 
                          className="user-name-link"
                          onClick={() => onUserNameClick && onUserNameClick(user.id)}
                        >
                          {user.firstName}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="user-username">@{user.username}</div>
                    </td>
                    <td>
                      <div className="user-id">{user.id}</div>
                    </td>
                    <td>
                      <span className={`hours-badge ${user.hours < 3 ? 'low' : ''}`}>
                        {user.hours} ч
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        {/* Actions */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showPagination && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UsersSection;
